import type { StageState, Tone } from '../kit'
import { statusWord } from '../../lib/status'
import { daysUntil } from '../../lib/api'
import type { ExitStageState, ExitSummary } from '../../lib/exit'

// The words and positions the Offboarding board and the exit workspace share.
//
// Nothing here is a rule. Which stages are in force, who may sign and what
// holds a stage all arrive from the API; this file turns those answers into a
// label, a date and the states a track draws, so the board card and the
// vendor's own workspace say the same thing in the same words.

export const TRIGGER_WORDS: Record<string, string> = {
  MANUAL: 'Raised by a person',
  CONTRACT_END: 'Contract ending',
  RISK_THRESHOLD: 'Risk threshold crossed',
  REGULATOR_INSTRUCTION: 'Regulator instruction',
}

// What each office confirms when it signs, in one line of at most 80
// characters. The full reason, with its regulatory basis, is the stage's own
// why from the API and lives behind the (i) and in the About drawer.
const STAGE_HELP: Record<string, string> = {
  BUSINESS_OWNER: 'confirms the service has moved or is no longer needed',
  CISO: 'confirms the access, keys and accounts the vendor held are closed',
  RISK_OFFICER: 'signs off the residual risk and that the exit plan was followed',
  DPO: 'confirms personal data is returned or erased',
  FINANCIAL_CLOSURE: 'closes the contract, the final invoices and any credits',
  CEO: 'gives the final sign-off for a service with no alternate provider',
}

export function stageHelp(key: string): string {
  return STAGE_HELP[key] ?? 'signs this stage of offboarding'
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "15 Sep" from an ISO day or instant, read as the calendar day it names.
export function dayMonth(iso: string | undefined | null): string {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  return m[3] + ' ' + (MONTHS[Number(m[2]) - 1] ?? '')
}

// "15 Sep 2026".
export function dayMonthYear(iso: string | undefined | null): string {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? dayMonth(iso) + ' ' + m[1] : iso
}

// "15 Sep, 10:40" in the reader's own zone, for a signature or a message.
export function stamp(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return String(d.getDate()).padStart(2, '0') + ' ' + (MONTHS[d.getMonth()] ?? '') + ', ' + hh + ':' + mm
}

// Today as an ISO day in the reader's zone.
export function todayISO(): string {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

// The worker proposes exits from facts in the register and signs as "worker".
export function proposerWords(subject: string | undefined, label: (s: string) => string): string {
  if (!subject) return 'Not recorded'
  if (subject === 'worker') return 'Raised automatically'
  return label(subject)
}

// A stored subject cut to what fits on a card: the part before the @.
export function shortPerson(label: string): string {
  const at = label.indexOf('@')
  return at > 0 ? label.slice(0, at) : label
}

// Two stored subjects or role codes name the same thing, whatever their case.
export function same(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

export function truncate(text: string, limit: number): string {
  const t = text.trim()
  return t.length <= limit ? t : t.slice(0, limit - 1).trimEnd() + '…'
}

// The states a track draws for an exit on the board: signed is done, a
// refusal is rejected, the stage the exit has reached is current (or blocked
// when something holds it), and the rest are upcoming. A proposal has reached
// no stage, so every stage is upcoming.
export function trackStates(x: ExitSummary): StageState[] {
  return x.stages.map((s, i) => {
    if (s.state === 'signed') return 'done'
    if (s.state === 'declined') return 'rejected'
    if (x.laneState === 'UNDER_WAY' && i === x.stageIndex) return x.blocker ? 'blocked' : 'current'
    return 'upcoming'
  })
}

export function trackLabel(x: ExitSummary): string {
  if (x.stageCount === 0) return 'No sign-off on record'
  if (x.laneState === 'PROPOSED') return 'Not started, ' + x.stageCount + ' stages'
  if (x.stageIndex >= x.stageCount) return 'All ' + x.stageCount + ' stages signed'
  return 'Stage ' + (x.stageIndex + 1) + ' of ' + x.stageCount + (x.currentJob ? ', with ' + x.currentJob : '')
}

// The one status a card carries, in the vocabulary's words (lib/status.ts):
// Proposed, Offboarding, Closed, and the overlays Blocked, Overdue, Due soon.
// "Needs a decision", "Under way" and "Signed out" were second and third names
// for the same states; whether a proposal is the reader's to decide is a row in
// My work, not a different word on the card.
function ew(code: string): { tone: Tone; text: string } {
  const s = statusWord('exit', code)
  return { tone: s.tone, text: s.word }
}

export function exitStatus(x: ExitSummary, mayDecide: boolean): { tone: Tone; text: string } {
  switch (x.laneState) {
    case 'PROPOSED':
      return mayDecide ? ew('PROPOSED') : { ...ew('PROPOSED'), tone: 'neutral' }
    case 'SIGNED_OUT':
      return ew('CLOSED')
  }
  if (x.blocker) return ew('BLOCKED')
  const d = daysUntil(x.dueOn)
  if (d !== null && d < 0) return ew('OVERDUE')
  if (d !== null && d <= 2) return ew('DUE_SOON')
  return ew('OPEN')
}

// The one status a vendor's exit carries at the top of its workspace and on its
// summary in the vendor record. The board's summary decides it where the API
// returned one; without it, the vendor's own status and chain do.
export function exitWord(
  summary: ExitSummary | null,
  status: string,
  proposed: boolean,
  allSigned: boolean,
  mayDecide: boolean,
): { tone: Tone; text: string } {
  let word: { tone: Tone; text: string } = ew('NONE')
  if (summary) word = exitStatus(summary, mayDecide)
  else if (status === 'OFFBOARDED') word = ew('CLOSED')
  else if (proposed) word = mayDecide ? ew('PROPOSED') : { ...ew('PROPOSED'), tone: 'neutral' }
  else if (status === 'OFFBOARDING') word = ew('OPEN')
  if (status === 'OFFBOARDING' && allSigned && word.text === ew('OPEN').text) word = ew('ALL_SIGNED')
  return word
}

// How long an exit has been open, against its plan where there is one.
export function openFor(daysOpen: number | undefined, plannedDays: number | undefined): string {
  if (daysOpen === undefined) return ''
  if (plannedDays) return 'Day ' + (daysOpen + 1) + ' of ' + plannedDays
  if (daysOpen === 0) return 'Opened today'
  return 'Open ' + daysOpen + (daysOpen === 1 ? ' day' : ' days')
}

// Who has the exit now, in a few words.
export function nowWith(x: ExitSummary, label: (s: string) => string): string {
  switch (x.laneState) {
    case 'PROPOSED':
      return shortPerson(proposerWords(x.proposedBy, label)) + (x.proposedAt ? ', ' + dayMonth(x.proposedAt) : '')
    case 'SIGNED_OUT':
      return x.closedAt ? 'Closed ' + dayMonth(x.closedAt) : 'Closed'
  }
  if (!x.currentJob) return 'Every stage signed'
  return x.currentJob + (x.dueOn ? ', due ' + dayMonth(x.dueOn) : '')
}

// The chain the vendor page reads, turned into the board's shape, for an API
// that has no GET /v1/exits yet. See components/exit/board.ts.
export function stagePosition(stages: ExitStageState[]): number {
  const i = stages.findIndex((s) => s.state !== 'signed')
  return i < 0 ? stages.length : i
}
